import { AnimatePresence, motion } from 'framer-motion'
import { CalendarClock, ChevronRight, Pencil, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import {
  isMemberAppointmentChangeWindowClosed,
  type PublicAppointment,
  type PublicPatient,
  type Region,
} from '@snootsvet/shared/portal'

import { AppointmentChangeWindowClosedAlert } from '@/customer/components/booking/appointment-change-window-closed-alert'
import { BookingContextCard } from '@/customer/components/booking/booking-context-card'
import { Button } from '@/customer/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/customer/components/ui/dialog'
import { useUpdateAppointment } from '@/customer/hooks/use-update-appointment'
import { routes } from '@/customer/routes'

type ManageAppointmentDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  appointment: PublicAppointment
  pet: PublicPatient
  /** Locale + hour convention for the summary card inside the dialog. */
  region: Region
}

/**
 * Manage-existing-appointment dialog. Opened from any surface that
 * shows an upcoming visit (currently `UpcomingVisitCard` on the
 * account home). Composes:
 *
 * - a `BookingContextCard` header summarising the visit;
 * - a `Reschedule` action row that navigates to the reschedule page;
 * - a second action row that swaps once the change deadline has passed.
 *
 * The cancel path uses an in-dialog "are you sure?" step reached via
 * a horizontal wipe transition. Both views live inside the same
 * `DialogContent` so the modal doesn't dismount / remount around the
 * confirmation.
 */
export function ManageAppointmentDialog({
  open,
  onOpenChange,
  appointment,
  pet,
  region,
}: ManageAppointmentDialogProps) {
  const navigate = useNavigate()
  const [view, setView] = useState<'default' | 'confirm-cancel'>('default')
  const updateAppointment = useUpdateAppointment(appointment.id)

  // Reset the view whenever the dialog closes so the next open lands
  // on the default panel rather than mid-confirmation. Runs after the
  // close animation via the effect so users don't watch the panels
  // slide as the dialog fades out.
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setView('default')
        updateAppointment.reset()
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [open, updateAppointment.reset])

  const start = new Date(appointment.startTime)
  const vetName = `${appointment.staff.firstName} ${appointment.staff.lastName.charAt(0)}.`
  const isEditable = !isMemberAppointmentChangeWindowClosed(start)

  const handleReschedule = () => {
    onOpenChange(false)
    navigate(
      generatePath(routes.account.appointments.reschedule, {
        appointmentId: appointment.id,
      })
    )
  }

  const handleConfirmCancel = () => {
    updateAppointment.mutate(
      { action: 'cancel' },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader tone="yellow">
          <CalendarClock className="size-6" />
          <DialogTitle>Your appointment</DialogTitle>
        </DialogHeader>
        {/*
         * Both views live inside a single body wrapper. `overflow-hidden`
         * clips the sliding panel while it's mid-transition, and the
         * `AnimatePresence mode="wait"` swap keeps the dialog height
         * tracking the visible child.
         */}
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            {view === 'default' ? (
              <motion.div
                key="default"
                initial={{ x: -24, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -24, opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                <DialogBody>
                  <BookingContextCard
                    appointmentTypeId={appointment.appointmentTypeId}
                    clinicId={appointment.clinicId}
                    start={start}
                    region={region}
                    petName={pet.name}
                    petSpecies={pet.species}
                    vetName={vetName}
                  />

                  {isEditable ? (
                    <div className="flex flex-col gap-3">
                      <Button
                        variant="default"
                        align="start"
                        className="gap-3 px-4 py-4 h-auto"
                        onClick={handleReschedule}
                      >
                        <Pencil className="size-5" aria-hidden />
                        <span className="flex-1">Reschedule</span>
                        <ChevronRight
                          className="size-5 shrink-0 transition-transform group-hover/button:translate-x-0.5"
                          aria-hidden
                        />
                      </Button>
                      <Button
                        variant="destructive"
                        align="start"
                        className="gap-3 px-4 py-4 h-auto"
                        onClick={() => setView('confirm-cancel')}
                      >
                        <X className="size-5" aria-hidden />
                        <span className="flex-1">Cancel appointment</span>
                        <ChevronRight
                          className="size-5 shrink-0 transition-transform group-hover/button:translate-x-0.5"
                          aria-hidden
                        />
                      </Button>
                    </div>
                  ) : (
                    <AppointmentChangeWindowClosedAlert clinicId={appointment.clinicId} />
                  )}
                </DialogBody>
              </motion.div>
            ) : (
              <motion.div
                key="confirm-cancel"
                initial={{ x: 24, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 24, opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                <DialogBody>
                  <div className="flex flex-col gap-2">
                    <h2 className="text-headline-sm uppercase leading-tight">
                      Cancel this appointment?
                    </h2>
                    <p className="text-body-md text-muted-foreground">
                      We'll let the clinic know straight away. You can always book another visit
                      when you're ready.
                    </p>
                  </div>

                  <BookingContextCard
                    appointmentTypeId={appointment.appointmentTypeId}
                    clinicId={appointment.clinicId}
                    start={start}
                    region={region}
                    petName={pet.name}
                    petSpecies={pet.species}
                    vetName={vetName}
                  />

                  {updateAppointment.error ? (
                    <p className="text-body-sm text-destructive">
                      {updateAppointment.error.message || 'Something went wrong. Please try again.'}
                    </p>
                  ) : null}

                  <div className="flex flex-col-reverse gap-3 sm:flex-row">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:flex-1"
                      onClick={() => setView('default')}
                      disabled={updateAppointment.isPending}
                    >
                      Keep appointment
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      className="w-full sm:flex-1"
                      onClick={handleConfirmCancel}
                      disabled={updateAppointment.isPending}
                    >
                      {updateAppointment.isPending ? 'Cancelling…' : 'Yes, cancel'}
                    </Button>
                  </div>
                </DialogBody>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  )
}
